import React, { FC, HTMLAttributes } from "react";
import styled from "styled-components";
import Paragraph from "./Paragraph";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { Icon, SvgIconTypeMap } from "@mui/material";
import { VoiceChatSharp } from "@mui/icons-material";

interface LabelDisplayedRowsArgs {
  from: number;
  to: number;
  count: number;
  page: number;
}

interface PaginationProps extends HTMLAttributes<HTMLDivElement> {
  count: number;
  rowsPerPage: number;
  page: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    page: number,
  ) => void;
  onRowsPerPageChange?: React.ChangeEventHandler<HTMLSelectElement>;
  rowsPerPageOptions?: ReadonlyArray<number | { value: number; label: string }>;
  labelRowsPerPage?: React.ReactNode;
  labelDisplayedRows?: (
    paginationInfo: LabelDisplayedRowsArgs,
  ) => React.ReactNode;
  backIconButtonProps?: {
    icon: React.ReactNode;
    content?: string;
  };
  nextIconButtonProps?: {
    icon: React.ReactNode;
    content?: string;
  };
}

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-top: 1px solid ${({ theme }) => theme.border};
  /* background-color: ${({ theme }) => theme.secondaryBackground}; */
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Select = styled.select`
  padding: 4px 8px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.text};
  background-color: transparent;
  border: none;
  border-radius: 4px;
`;

const Button = styled.button`
  padding: 4px 8px;
  font-size: 0.875rem;

  background-color: transparent;
  border: none;
  border-radius: 4px;
  color: ${({ theme }) => theme.text};
  cursor: pointer;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* background-color: ${({ theme }) => theme.secondaryBackground}; */
  /* border: 1px solid ${({ theme }) => theme.border}; */
`;

const TablePagination: FC<PaginationProps> = ({
  count,
  rowsPerPage,
  page,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
  labelRowsPerPage,
  labelDisplayedRows,
  backIconButtonProps,
  nextIconButtonProps,
  ...props
}) => {
  const totalPages = Math.ceil(count / rowsPerPage);
  const from = page * rowsPerPage + 1;
  const to = Math.min((page + 1) * rowsPerPage, count);

  return (
    <PaginationContainer {...props}>
      <ControlsContainer>
        {onRowsPerPageChange && (
          <>
            <Paragraph
              style={{
                minWidth: "150px",
                justifyContent: "end",
              }}
            >
              {labelRowsPerPage}
            </Paragraph>
            <Select value={rowsPerPage} onChange={onRowsPerPageChange}>
              {rowsPerPageOptions.map(option =>
                typeof option === "number" ? (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ) : (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ),
              )}
            </Select>
          </>
        )}
        {labelDisplayedRows && (
          <Paragraph>{labelDisplayedRows({ from, to, count, page })}</Paragraph>
        )}
        <Button
          {...backIconButtonProps}
          onClick={e => onPageChange(e, page - 1)}
          disabled={page === 0}
          aria-label="Página anterior"
        >
          {backIconButtonProps?.icon}
          {backIconButtonProps?.content}
        </Button>
        <Button
          onClick={e => onPageChange(e, page + 1)}
          disabled={page >= totalPages - 1}
        >
          {nextIconButtonProps?.icon}
          {nextIconButtonProps?.content}
        </Button>
      </ControlsContainer>
    </PaginationContainer>
  );
};

export default TablePagination;
